import { useEffect, type RefObject } from 'react';

/** A mouse that moved further than this (px) dragged the row, not clicked it. */
const CLICK_SLOP_PX = 5;
/** A wheel stepping by lines rather than pixels moves this far a line. */
const WHEEL_LINE_PX = 16;

/**
 * Lets a mouse scroll a sideways row whose scrollbar is hidden, which it
 * otherwise cannot on desktop. A mouse drags the row the way a finger swipes
 * it, and a drag that moved the row swallows the click it ends with. A
 * vertical wheel turned over it moves it sideways while it has room that
 * way; at its end the page scrolls on. Touch is left to the browser.
 *
 * `count` is how many items the row holds: the listeners re-attach when the
 * row is rebuilt.
 */
export function useWheelDragScroll(ref: RefObject<HTMLElement | null>, count: number): void {
  useEffect(() => {
    const row = ref.current;
    if (!row) return;

    const turnWheel = (event: WheelEvent) => {
      // A sideways touchpad swipe and a pinch are left to the browser.
      if (event.ctrlKey || Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      const end = row.scrollWidth - row.clientWidth;
      const isPastEdge =
        end <= 0 || (event.deltaY < 0 && row.scrollLeft <= 0) || (event.deltaY > 0 && row.scrollLeft >= end - 1);
      if (isPastEdge) return;
      event.preventDefault();
      row.scrollLeft += event.deltaMode === 1 ? event.deltaY * WHEEL_LINE_PX : event.deltaY;
    };

    // Ends a mouse drag in progress; null while there is none.
    let endDrag: (() => void) | null = null;
    let hasDragged = false;

    // The listeners sit on the window, so a release outside the row is heard.
    const startDrag = (event: PointerEvent) => {
      hasDragged = false;
      if (event.pointerType !== 'mouse' || event.button !== 0 || row.scrollWidth <= row.clientWidth) return;
      const startX = event.clientX;
      const startLeft = row.scrollLeft;
      const controller = new AbortController();
      const { signal } = controller;
      endDrag = () => {
        controller.abort();
        endDrag = null;
        row.style.cursor = '';
      };
      const move = (moveEvent: PointerEvent) => {
        // The button let go somewhere the release was never heard.
        if (moveEvent.buttons % 2 === 0) {
          endDrag?.();
          return;
        }
        const shift = moveEvent.clientX - startX;
        if (!hasDragged && Math.abs(shift) <= CLICK_SLOP_PX) return;
        hasDragged = true;
        row.style.cursor = 'grabbing';
        row.scrollLeft = startLeft - shift;
      };
      window.addEventListener('pointermove', move, { signal });
      window.addEventListener('pointerup', () => endDrag?.(), { signal });
      window.addEventListener('pointercancel', () => endDrag?.(), { signal });
    };

    const swallowClick = (event: MouseEvent) => {
      if (!hasDragged) return;
      hasDragged = false;
      event.preventDefault();
      event.stopPropagation();
    };

    // A picture or a selection would start the browser's own drag instead.
    const preventNativeDrag = (event: DragEvent) => event.preventDefault();

    row.addEventListener('pointerdown', startDrag);
    row.addEventListener('click', swallowClick, true);
    row.addEventListener('dragstart', preventNativeDrag);
    row.addEventListener('wheel', turnWheel, { passive: false });

    return () => {
      endDrag?.();
      row.removeEventListener('pointerdown', startDrag);
      row.removeEventListener('click', swallowClick, true);
      row.removeEventListener('dragstart', preventNativeDrag);
      row.removeEventListener('wheel', turnWheel);
    };
  }, [ref, count]);
}
